"use client";

import { useEffect, useMemo, useRef } from "react";

import { sanitizeRenderFragment } from "./parse-render-blocks";

function buildHostDocument(fragment: string, isDark: boolean): string {
  const safe = sanitizeRenderFragment(fragment);
  const bg = isDark ? "#0c0c0e" : "#ffffff";
  const text = isDark ? "#f4f4f5" : "#18181b";
  const textSecondary = isDark ? "#a1a1aa" : "#52525b";
  const border = isDark ? "#3f3f46" : "#e4e4e7";
  const surface = isDark ? "#18181b" : "#ffffff";
  const surface2 = isDark ? "#27272a" : "#f4f4f5";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.34.1/tabler-icons.min.css" />
<script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js"><\/script>
<script src="https://cdn.jsdelivr.net/npm/d3@7.9.0/dist/d3.min.js"><\/script>
<style>
  :root {
    --color-background-primary: ${surface};
    --color-background-secondary: ${surface2};
    --color-background-tertiary: ${isDark ? "#3f3f46" : "#e4e4e7"};
    --color-background-info: ${isDark ? "#0c447c" : "#E6F1FB"};
    --color-background-success: ${isDark ? "#27500A" : "#EAF3DE"};
    --color-background-warning: ${isDark ? "#633806" : "#FAEEDA"};
    --color-background-danger: ${isDark ? "#791F1F" : "#FCEBEB"};
    --color-text-primary: ${text};
    --color-text-secondary: ${textSecondary};
    --color-text-tertiary: ${isDark ? "#71717a" : "#71717a"};
    --color-text-info: ${isDark ? "#85B7EB" : "#185FA5"};
    --color-text-success: ${isDark ? "#97C459" : "#3B6D11"};
    --color-text-warning: ${isDark ? "#EF9F27" : "#854F0B"};
    --color-text-danger: ${isDark ? "#F09595" : "#A32D2D"};
    --color-border-tertiary: ${border};
    --color-border-secondary: ${isDark ? "#52525b" : "#d4d4d8"};
    --color-border-primary: ${isDark ? "#a1a1aa" : "#a1a1aa"};
    --color-border-info: #378ADD;
    --color-border-success: #639922;
    --color-border-warning: #BA7517;
    --color-border-danger: #E24B4A;
    --font-sans: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
    --font-serif: ui-serif, Georgia, Cambria, Times New Roman, Times, serif;
    --font-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    --border-radius-md: 8px;
    --border-radius-lg: 12px;
    --border-radius-xl: 16px;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: ${bg};
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.55;
  }
  #root { padding: 4px 0; overflow-x: auto; }
  /* Widget titles — 16px to match InsightsMessageText answer headings. */
  #root h1,
  #root h2,
  #root h3,
  #root h4,
  #root h5,
  #root h6 {
    font-size: 16px !important;
    font-weight: 600 !important;
    line-height: 1.35 !important;
    letter-spacing: -0.01em;
    color: var(--color-text-primary);
  }
  /* Chart.js needs a fixed-height wrapper; static HTML cards must not inherit that habit. */
  #root > *:not(:has(canvas)) {
    height: auto !important;
    min-height: 0 !important;
  }
  /* Tables: size columns to content and scroll horizontally — never squash mid-word.
     Caller: InsightsAnswer → InsightsRenderFrame (HTML <render>). */
  #root table {
    width: max-content;
    max-width: none;
    min-width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }
  #root th,
  #root td {
    white-space: nowrap;
    overflow-wrap: normal;
    word-break: normal;
    vertical-align: top;
    padding: 6px 10px;
  }
  #root th[data-wrap],
  #root td[data-wrap],
  #root td.wrap,
  #root th.wrap {
    white-space: normal;
    max-width: 18rem;
    overflow-wrap: break-word;
  }
</style>
<script>
  window.__isDark = ${isDark ? "true" : "false"};
  window.__gridColor = ${JSON.stringify(isDark ? "#3f3f46" : "#e4e4e7")};
  window.__textColor = ${JSON.stringify(textSecondary)};
  window.whenChartReady = function(cb) {
    function run() {
      if (window.Chart) { try { cb(); } catch (e) { console.error(e); } return; }
      setTimeout(run, 30);
    }
    run();
  };
<\/script>
</head>
<body>
<div id="root">${safe}</div>
<script>
  function wrapTables() {
    var root = document.getElementById('root');
    if (!root) return;
    root.querySelectorAll('table').forEach(function(table) {
      // Agent fragments often set width:100% / max-width:100% which squashes columns.
      table.style.width = 'max-content';
      table.style.maxWidth = 'none';
      table.style.minWidth = '100%';
      table.style.tableLayout = 'auto';
      table.querySelectorAll('th, td').forEach(function(cell) {
        var text = (cell.textContent || '').trim();
        var isLong =
          text.length > 48 ||
          /why|reason|note|detail|comment|description/i.test(
            (cell.closest('table') &&
              cell.cellIndex != null &&
              cell.closest('table').querySelectorAll('th')[cell.cellIndex]
                ? cell.closest('table').querySelectorAll('th')[cell.cellIndex].textContent
                : '') || '',
          );
        if (isLong && cell.tagName === 'TD') {
          cell.style.whiteSpace = 'normal';
          cell.style.maxWidth = '18rem';
          cell.style.overflowWrap = 'break-word';
        } else {
          cell.style.whiteSpace = 'nowrap';
          cell.style.overflowWrap = 'normal';
          cell.style.wordBreak = 'normal';
        }
      });
      var parent = table.parentElement;
      if (!parent || parent === root) {
        var wrap = document.createElement('div');
        wrap.setAttribute('data-insights-table-scroll', '1');
        wrap.style.overflowX = 'auto';
        wrap.style.maxWidth = '100%';
        wrap.style.webkitOverflowScrolling = 'touch';
        table.parentNode.insertBefore(wrap, table);
        wrap.appendChild(table);
        return;
      }
      var style = window.getComputedStyle(parent);
      if (style.overflowX === 'auto' || style.overflowX === 'scroll') return;
      if (parent.getAttribute('data-insights-table-scroll') === '1') return;
      parent.style.overflowX = 'auto';
      parent.style.maxWidth = '100%';
      parent.setAttribute('data-insights-table-scroll', '1');
    });
  }
  function measureHeight() {
    var root = document.getElementById('root');
    if (!root) return 40;
    return Math.max(
      Math.ceil(root.getBoundingClientRect().height),
      Math.ceil(root.scrollHeight),
      40
    );
  }
  function notifyHeight() {
    try {
      window.parent.postMessage({ type: 'insights-render-resize', height: measureHeight() }, '*');
    } catch (e) {}
  }
  function boot() {
    wrapTables();
    notifyHeight();
    var root = document.getElementById('root');
    if (root && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(function() { notifyHeight(); }).observe(root);
    }
    setTimeout(notifyHeight, 100);
    setTimeout(notifyHeight, 500);
  }
  if (document.readyState === 'loading') {
    window.addEventListener('load', boot);
  } else {
    boot();
  }
<\/script>
</body>
</html>`;
}

export function InsightsRenderFrame({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isDark =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  const srcDoc = useMemo(() => buildHostDocument(html, isDark), [html, isDark]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const data = event.data as { type?: string; height?: number };
      if (data?.type !== "insights-render-resize" || typeof data.height !== "number") return;
      if (!iframeRef.current) return;
      if (event.source !== iframeRef.current.contentWindow) return;
      // Fit content; small floor so empty frames do not collapse to zero.
      iframeRef.current.style.height = `${Math.max(48, Math.ceil(data.height) + 4)}px`;
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="Insights visual"
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      className="my-3 w-full overflow-hidden rounded-xl border border-border/60 bg-background"
      style={{ height: 80, border: 0 }}
    />
  );
}
